import React from 'react';

import countries from 'country-code-lookup';
import countriesData from 'country-region-data/data.json';
import { setIn, useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { Col, Form, Row } from 'react-bootstrap';

import { useAppSelector } from 'hooks';
import { Input, Select } from 'components/Forms';
import { getCountryCodeByGid } from 'store/groups/reducer';
import { formatDropdownOptions } from 'utils/Object';

import { AddressAutocomplete } from '.';

interface IAddressComponent {
  defaultAddress?: IAddress;
  gid: string;
  formSchema?: string;
  disabled?: boolean;
  required?: boolean;
  patientAddress?: boolean;
}

const Address = ({
  gid,
  defaultAddress,
  formSchema,
  disabled,
  required = true,
  patientAddress = false
}: IAddressComponent) => {
  const intl = useIntl();
  const countryCode = useAppSelector((state) => getCountryCodeByGid(state, gid));
  const countryCodeIso2 = countries.byIso(countryCode)?.iso2;
  const country = countriesData.find((x) => x.countryShortCode === countryCodeIso2);
  const { values, setValues } = useFormikContext<any>();
  formSchema = formSchema ? formSchema + '.' : '';

  const handleAddress = (address: IAddress) => {
    const newValues = [
      [`${formSchema}country_code`, countryCodeIso2 ?? ''],
      [`${formSchema}street_address_line_1`, address.street_address_line_1],
      [`${formSchema}city`, address.city],
      [`${formSchema}state_province`, address.state_province],
      [`${formSchema}postal_code`, address.postal_code]
    ].reduce((acc, [path, val]) => setIn(acc, path, val), values);
    setValues(newValues, true);
  };

  return (
    <>
      <Row>
        <>
          {!patientAddress && (
            <Form.Group className="form-group" as={Col} md={6}>
              <Input
                type="text"
                placeholder={intl.formatMessage({ id: 'clinicShippingPlaceholderLabelKey' })}
                label={intl.formatMessage({ id: 'clinicShippingInfoLabelKey' })}
                name={`${formSchema}company`}
                disabled={disabled}
              />
            </Form.Group>
          )}
        </>
      </Row>
      <Row>
        <Form.Group className="form-group" as={Col} md={6}>
          <Form.Label>
            {intl.formatMessage({ id: 'addressKey' })} {required && '*'}
          </Form.Label>
          <AddressAutocomplete
            name={`${formSchema}street_address_line_1`}
            onChange={handleAddress}
            gid={gid}
            short
            defaultAddress={defaultAddress || null}
            disabled={disabled}
            required={required}
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md={6}>
          <Input
            label={intl.formatMessage({ id: 'address2Key' })}
            name={`${formSchema}street_address_line_2`}
            disabled={disabled}
          />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group className="form-group" as={Col} md={6}>
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'cityKey' })}
            label={intl.formatMessage({ id: 'cityKey' })}
            name={`${formSchema}city`}
            disabled={disabled}
            required={required}
          />
        </Form.Group>
        {countryCode !== 'SWE' && (
          <Form.Group className="form-group" as={Col} md={6}>
            <Select
              placeholder={intl.formatMessage({ id: 'stateProvinceKey' })}
              label={intl.formatMessage({ id: 'stateProvinceKey' })}
              name={`${formSchema}state_province`}
              options={formatDropdownOptions(country?.regions, 'name', 'name')}
              isDisabled={disabled}
              required={required}
              isClearable={!required}
              dataTestId="state-province-select"
            />
          </Form.Group>
        )}
        <Form.Group className="form-group" as={Col} md={6}>
          <Input
            type="text"
            placeholder={intl.formatMessage({ id: 'zipCodeKey' })}
            label={intl.formatMessage({ id: 'zipCodeKey' })}
            name={`${formSchema}postal_code`}
            disabled={disabled}
            required={required}
          />
        </Form.Group>
      </Row>
    </>
  );
};

export default Address;
